import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RiBugLine, RiCloseLine } from 'react-icons/ri';
import './index.css';
import './i18n';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { AuthProvider, useAuth } from './contexts/auth-context';
import { BrandingProvider } from './contexts/branding-context';
import {
  DebugToolsProvider,
  useDebugTools,
} from './contexts/debug-tools-context';
import { SocketProvider } from './contexts/socket-context';
import { ThemeProvider } from './contexts/theme-context';
import { STATUS } from './utils/utils';
import { initGoogleAnalytics, sendWebVitalToGa4 } from './utils/analytics';

const LazyReactQueryDevtoolsPanel = lazy(() =>
  import('react-query/devtools').then((mod) => ({
    default: mod.ReactQueryDevtoolsPanel,
  }))
);

initGoogleAnalytics();

const root = ReactDOM.createRoot(document.getElementById('root'));
// Nao retentar erros 4xx (permanentes do lado do cliente — 404 deleted, 403 censura, 401 etc.)
const retryPolicy = (failureCount, error) => {
  const status = error?.response?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 1;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: retryPolicy,
    },
  },
});

const AdminOnlyQueryDevtools = () => {
  const { isAuthenticated, user, status } = useAuth();
  const { isDebugToolsOpen, closeDebugTools } = useDebugTools();

  if (status === STATUS.PENDING) {
    return null;
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return null;
  }

  return (
    isDebugToolsOpen && (
      <div className="fixed inset-x-0 bottom-0 z-[390] h-[46vh] min-h-[320px] max-h-[70vh] border-t border-on-surface/20 bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-on-surface/10 px-4 py-2">
          <div className="flex items-center gap-2 text-on-surface">
            <RiBugLine size={18} />
            <span className="text-sm font-semibold">Painel de debug</span>
          </div>
          <button
            type="button"
            onClick={closeDebugTools}
            aria-label="Fechar painel de debug"
            title="Fechar painel de debug"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface/70 transition-colors hover:bg-on-surface/10 hover:text-on-surface"
          >
            <RiCloseLine size={18} />
          </button>
        </div>
        <div className="h-[calc(100%-49px)] overflow-hidden">
          <Suspense fallback={null}>
            <LazyReactQueryDevtoolsPanel />
          </Suspense>
        </div>
      </div>
    )
  );
};

root.render(
  <React.StrictMode>
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrandingProvider>
            <AuthProvider>
              <DebugToolsProvider>
                <SocketProvider>
                  <App />
                  <AdminOnlyQueryDevtools />
                </SocketProvider>
              </DebugToolsProvider>
            </AuthProvider>
          </BrandingProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Router>
  </React.StrictMode>
);

reportWebVitals(sendWebVitalToGa4);
